import { useCallback, useEffect, useState } from "react";

const KEYS = {
  compare: "skolvyn:compare",
} as const;

type StoreKey = keyof typeof KEYS;

function readList(key: StoreKey): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEYS[key]);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function writeList(key: StoreKey, list: string[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(KEYS[key], JSON.stringify(list));
  window.dispatchEvent(new CustomEvent(`skolvyn-store-${key}`));
}

function useList(key: StoreKey, max?: number) {
  const [list, setList] = useState<string[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setList(readList(key));
    setHydrated(true);
    const sync = () => setList(readList(key));
    window.addEventListener(`skolvyn-store-${key}`, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(`skolvyn-store-${key}`, sync);
      window.removeEventListener("storage", sync);
    };
  }, [key]);

  const has = useCallback((id: string) => list.includes(id), [list]);
  const toggle = useCallback((id: string) => {
    const current = readList(key);
    let next: string[];
    if (current.includes(id)) next = current.filter((x) => x !== id);
    else if (max && current.length >= max) return { added: false, atMax: true };
    else next = [...current, id];
    writeList(key, next);
    return { added: !current.includes(id), atMax: false };
  }, [key, max]);
  const remove = useCallback((id: string) => {
    writeList(key, readList(key).filter((x) => x !== id));
  }, [key]);
  const clear = useCallback(() => writeList(key, []), [key]);

  return { list, hydrated, has, toggle, remove, clear };
}

export const useCompare = () => useList("compare", 3);

// Auth bor i src/hooks/useAuthSession.ts — inte här. Den här filen är bara
// localStorage-hjälpare för jämförelselistan. Sparade skolor bor i
// src/lib/saved-schools.ts och sparade jobb i src/lib/saved-jobs.ts.
